'use client';

import { ColumnsType } from 'antd/es/table';
import {
  InputMaybe,
  PurchaseOrderPage,
  PurchaseOrderSortAttributeEnum,
  SortOrderEnum
} from 'gql/graphql';
import { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useContext } from 'react';
import { Control } from 'react-hook-form';
import useSearchParamsOrders, { FiltersOrdersForm } from './useSearchParamsOrders';
import useTableOrders, { ORDERS_KEYS_MAP, PurchaseOrderUnitary } from './useTableOrders';

type TOrdersContext = {
  orders?: PurchaseOrderPage;
  isLoading: boolean;
  isPending: boolean;
  isRetrieving: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  handleClear: () => void;
  control: Control<FiltersOrdersForm>;
  filtersAdded: boolean;
  columns: ColumnsType<PurchaseOrderUnitary>;
  columnsKeys: string[];
  columnsKeysFiltered: string[];
  handleOnClickBadge: (key: string) => () => void;
  sortAttribute?: InputMaybe<PurchaseOrderSortAttributeEnum>;
  sortOrder?: InputMaybe<SortOrderEnum>;
  ORDERS_KEYS_MAP: typeof ORDERS_KEYS_MAP;
  ordersUnitary: PurchaseOrderUnitary[];
};

const initialState = {
  isLoading: false,
  isPending: false,
  isRetrieving: false,
  page: 1,
  setPage: () => {},
  totalPages: 0,
  handleClear: () => {},
  control: {} as Control<FiltersOrdersForm>,
  filtersAdded: false,
  columns: [],
  columnsKeys: [],
  columnsKeysFiltered: [],
  handleOnClickBadge: () => () => {},
  sortAttribute: undefined,
  sortOrder: undefined,
  ORDERS_KEYS_MAP: ORDERS_KEYS_MAP,
  ordersUnitary: []
};

const OrdersContext = createContext<TOrdersContext>(initialState);

const useOrdersContext = (): TOrdersContext => useContext(OrdersContext);

const OrdersProvider: FC<PropsWithChildren<unknown>> = ({ children }): JSX.Element => {
  const {
    isLoading,
    isPending,
    page,
    setPage,
    totalPages,
    orders,
    isRetrieving,
    control,
    filtersAdded,
    handleClear,
    sortAttribute,
    sortOrder
  } = useSearchParamsOrders();

  const { columns, columnsKeysFiltered, columnsKeys, ordersUnitary, handleOnClickBadge } =
    useTableOrders({
      orders
    });

  return (
    <OrdersContext.Provider
      value={{
        ordersUnitary,
        orders,
        isLoading,
        isPending,
        isRetrieving,
        page,
        setPage,
        totalPages,
        handleClear,
        control,
        filtersAdded,
        columns,
        columnsKeysFiltered,
        columnsKeys,
        handleOnClickBadge,
        sortAttribute,
        sortOrder,
        ORDERS_KEYS_MAP
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
};

export { OrdersProvider, useOrdersContext };
